import type { Disposable, Maybe, Unsubscribe } from '@amazon/vinyl-util'
import { noop } from '@amazon/vinyl-util'
import type { ObservableValue } from '@amazon/vinyl-observable'

export interface CommandPayload {
    readonly id: string
    readonly type: string
    readonly payload?: unknown
}

export enum ConnectionStatus {
    DISCONNECTED = 'disconnected',
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    ERRED = 'erred',
}

export interface CommandSender {
    readonly connectionStatus: ObservableValue<ConnectionStatus>

    /**
     * True if connectionStatus is CONNECTED
     */
    canSend(): boolean

    /**
     * Sends a command.
     * If canSend() returns false, this will throw an IllegalStateException.
     */
    send(command: CommandPayload): void
}

/**
 * Invokes a callback when the bridge is connected.
 *
 * @param status The data provider for connection status.
 * @param cb Invoked when the status is CONNECTED. May return a callback to invoke when
 * no longer connected.
 */
export function onConnected(
    status: ObservableValue<ConnectionStatus>,
    cb: () => Unsubscribe | void
): Unsubscribe {
    let sub: Maybe<Unsubscribe> | void = null
    return status.onData((v) => {
        if (v === ConnectionStatus.CONNECTED) {
            if (!sub) sub = cb()
        } else {
            if (sub) {
                sub()
                sub = null
            }
        }
    })
}

export type CommandHandler = (command: CommandPayload) => void

export interface CommandReceiver extends Disposable {
    listen(handler: CommandHandler): Unsubscribe
}

export interface CommandBridge extends CommandSender, CommandReceiver {}

/**
 * Wraps a command bridge, prepending send commands with a specified key and
 * filtering listen handlers for that key.
 * This provides a way for a bridge to listen/send commands with hierarchical
 * handling.
 *
 * @param bridge
 * @param key A key to distinguish commands for filtering.
 */
export function createSubCommandBridge(bridge: CommandBridge, key: string): CommandBridge {
    const prefix = key + ':'
    return {
        get connectionStatus(): ObservableValue<ConnectionStatus> {
            return bridge.connectionStatus
        },

        listen(handler: CommandHandler): Unsubscribe {
            return bridge.listen((payload): void => {
                if (payload.type.startsWith(prefix)) {
                    handler({
                        ...payload,
                        type: payload.type.substring(prefix.length),
                    })
                }
            })
        },

        canSend(): boolean {
            return bridge.canSend()
        },

        send(command: CommandPayload): void {
            bridge.send({
                ...command,
                type: prefix + command.type,
            })
        },

        dispose: noop,
    }
}
